/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

import React from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';

const FEATURES = [
  {
    icon: '👩‍🏫',
    title: 'What your faculty knows',
    body: 'Showcase faculty expertise — decades of research, teaching, and industry practice made visible to the world.'
  },
  {
    icon: '🎓',
    title: 'What your students earn',
    body: "Track student capability across domains — not just transcripts, but actual demonstrated knowledge."
  },
  {
    icon: '🏭',
    title: 'Industry partnerships',
    body: 'Connect with employers seeking your graduates and partners for sponsored research.'
  },
  {
    icon: '🔬',
    title: 'Research, surfaced',
    body: 'Your departments\' work — visible to the industries that need it most.'
  }
];

const DEMO_MAILTO =
  'mailto:info@skilldelivery.co.in?subject=Early%20access%20-%20University';

const UniversityLanding = () => {
  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-brand-bgOff via-white to-white">

      <PublicNav />

      {/* ─── Decorative radial glows ────────────────────────────── */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -right-32"
        style={{
          width: '500px',
          height: '500px',
          background:
            'radial-gradient(circle, rgba(92, 225, 230, 0.15) 0%, transparent 70%)'
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-24 -left-24"
        style={{
          width: '400px',
          height: '400px',
          background:
            'radial-gradient(circle, rgba(33, 150, 243, 0.08) 0%, transparent 70%)'
        }}
      />

      <div className="relative max-w-6xl mx-auto px-6 pt-16 sm:pt-20 pb-16">

        {/* ── Back link ── */}
        <div className="mt-10">
          <Link
            to="/welcome"
            className="text-sm text-brand-cyan hover:text-brand-cyanDark font-medium hover:underline"
          >
            ← Back to home
          </Link>
        </div>

        {/* ── Hero ── */}
        <header className="text-center mt-10 sm:mt-12">
          <p className="text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
            For Universities
          </p>
          <h1 className="mt-4 text-5xl sm:text-6xl font-extrabold tracking-tighter leading-tight bg-gradient-to-br from-brand-cyan to-brand-blue bg-clip-text text-transparent">
            Your institution's depth — visible to industry.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-brand-muted leading-relaxed max-w-2xl mx-auto">
            Decades of teaching, research, and graduates trained — captured into a footprint that industry actually sees.
          </p>
          <p className="mt-4 text-sm italic text-brand-text2 max-w-2xl mx-auto">
            Skills, Delivered!
          </p>
          <div className="mt-6 flex justify-center">
            <span className="rounded-brandPill bg-brand-bgOff2 text-brand-cyan px-4 py-1 text-xs font-bold uppercase tracking-wider">
              Coming Soon
            </span>
          </div>
        </header>

        {/* ── Feature grid ── */}
        <section
          aria-label="What you get"
          className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5"
        >
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="bg-white border border-brand-border rounded-brandCard p-6 transition-all duration-200 hover:border-brand-cyan hover:-translate-y-0.5 hover:shadow-brand"
            >
              <div className="w-11 h-11 rounded-xl bg-brand-cyan/10 flex items-center justify-center mb-4">
                <span className="text-2xl leading-none" aria-hidden="true">{f.icon}</span>
              </div>
              <h3 className="font-bold text-base text-brand-text">
                {f.title}
              </h3>
              <p className="mt-2 text-sm text-brand-muted leading-relaxed">
                {f.body}
              </p>
            </div>
          ))}
        </section>

        {/* ── Manifesto callout ── */}
        <section className="mt-16 text-center">
          <p className="text-sm text-brand-muted">
            <Link
              to="/manifesto"
              className="text-brand-cyan hover:text-brand-cyanDark font-medium hover:underline"
            >
              Read why SkillDelivery exists →
            </Link>
          </p>
        </section>

        {/* ── Final CTA ── */}
        <section className="mt-12 text-center">
          <p className="text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
            Want early access?
          </p>
          <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-brand-text tracking-tight">
            Reach out — we'd love your input.
          </h2>
          <p className="mt-4 text-base text-brand-muted leading-relaxed max-w-2xl mx-auto">
            We're designing the University footprint with input from early academic partners. Tell us what visibility means for your institution.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center">
            <a
              href={DEMO_MAILTO}
              className="inline-flex items-center justify-center px-6 py-3 rounded-brand bg-gradient-to-br from-brand-cyanLight to-brand-blue text-white font-semibold text-sm shadow-brandButton hover:shadow-brandButtonHover hover:-translate-y-px transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2"
            >
              Reach out for early access
            </a>
            <Link
              to="/tour"
              className="inline-flex items-center justify-center px-6 py-3 rounded-brand border-2 border-brand-cyan text-brand-cyan font-semibold text-sm bg-white hover:bg-brand-bgOff2 hover:-translate-y-px transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2"
            >
              Take the platform tour
            </Link>
          </div>
        </section>

        {/* ── Inline footer ── */}
        <footer className="text-center mt-16">
          <Link
            to="/welcome"
            className="text-sm text-brand-cyan hover:text-brand-cyanDark font-medium hover:underline"
          >
            ← Back to home
          </Link>
        </footer>

      </div>

      <PublicFooter />
    </div>
  );
};

export default UniversityLanding;
